import { Article, People } from "@mui/icons-material";
import { Box, ListItemDecorator, Stack, TabList, Tabs, Typography } from "@mui/joy";
import { Outlet, useMatchRoute } from "@tanstack/react-router";
import { ApiLeague } from "griptonite-api-client"; // ApiLeague type will need to be created
import { ReactNode, useState } from "react";

import { useLeagueQuery } from "@/hooks/competitions";
import { useCurrentVenueQuery, useCurrentVenueUuid } from "@/hooks/venues";

import { CompetitionsList } from "../CompetitionOverview/CompetitionOverview";
import EventsListToggle, {
  isCompactViewport,
  useCollapseEventsListOnSelect,
} from "../CompetitionOverview/EventsListToggle";
import { TabLink } from "../Links";

const LeagueOverview = ({
  leagueUuid,
  initialTab = "profile",
  children,
}: {
  leagueUuid?: string;
  initialTab?: "profile" | "competitors";
  children?: ReactNode;
}) => {
  const currentVenueUuid = useCurrentVenueUuid();

  const { data: venue } = useCurrentVenueQuery();

  const { data: league } = useLeagueQuery(leagueUuid, currentVenueUuid);

  let activeTab = initialTab;

  const matchRoute = useMatchRoute();
  const isCreate = !!matchRoute({
    to: "/gym/$venueUuid/events/series/create",
    fuzzy: true,
  });

  // Start collapsed on narrow screens once a series is selected. Without a selection the list
  // is the only content, so it stays open.
  const [leagueSelectorOpen, setLeagueSelectorOpen] = useState(
    () => !(isCompactViewport() && (!!leagueUuid || isCreate)),
  );
  useCollapseEventsListOnSelect(leagueUuid, () => setLeagueSelectorOpen(false));

  if (
    matchRoute({
      to: "/gym/$venueUuid/events/series/$leagueUuid/competitors",
      fuzzy: true,
    })
  ) {
    activeTab = "competitors";
  }

  const formatLeaguePeriod = (league: ApiLeague, _timezone: string) => {
    if (!league.startTime || !league.endTime) {
      return "–";
    }
    const startDate = new Date(league.startTime);
    const endDate = new Date(league.endTime);

    // Format dates to display in a readable format
    const startFormatted = startDate.toLocaleDateString();
    const endFormatted = endDate.toLocaleDateString();

    return `${startFormatted} - ${endFormatted}`;
  };

  return (
    <Box
      sx={{
        display: "flex",
        height: "100dvh",
        flexDirection: "column",
      }}
    >
      <Tabs value={activeTab}>
        <TabList
          sx={{
            flex: 1,
            ".MuiTab-root": {
              alignSelf: "flex-end",
            },
          }}
        >
          {isCreate || league ? (
            <Box
              sx={{
                width: 350,
                backgroundColor: "neutral.100",
                marginX: 1,
                marginY: 0.5,
                display: "flex",
                alignItems: "center",
                border: "1px solid",
                borderColor: "neutral.200",
                borderRadius: 8,
                paddingX: 1,
                cursor: "pointer",
                overflow: "hidden",
              }}
              onClick={() => setLeagueSelectorOpen(!leagueSelectorOpen)}
            >
              <Stack sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  level="body-lg"
                  fontWeight="bold"
                  sx={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    flex: 1,
                  }}
                >
                  {league?.name ?? (isCreate ? "Creating New Series" : "Events")}
                </Typography>
                <Stack direction="row">
                  <Typography level="body-sm" color="neutral">
                    {league ? formatLeaguePeriod(league, venue?.timezone || "UTC") : "Upcoming"}
                  </Typography>
                </Stack>
              </Stack>
              <EventsListToggle
                isOpen={leagueSelectorOpen}
                onToggle={() => setLeagueSelectorOpen(!leagueSelectorOpen)}
              />
            </Box>
          ) : (
            <Box
              sx={{
                width: 350,
                marginX: 1,
                marginY: 0.5,
                display: "flex",
                alignItems: "center",
                borderRadius: 8,
                paddingX: 1,
                cursor: "pointer",
                overflow: "hidden",
              }}
            >
              <Typography level="body-lg" fontWeight="bold">
                Events
              </Typography>
            </Box>
          )}
          <TabLink
            value="profile"
            sx={{ flex: 1, height: 40 }}
            from="/gym/$venueUuid"
            to={
              leagueUuid
                ? "/gym/$venueUuid/events/series/$leagueUuid/profile"
                : "/gym/$venueUuid/events/series/create"
            }
            params={(prev) => ({ ...prev, leagueUuid })}
            replace
          >
            <ListItemDecorator>
              <Article />
            </ListItemDecorator>
            Profile
          </TabLink>
          <TabLink
            value="competitors"
            sx={{ flex: 1, height: 40 }}
            from="/gym/$venueUuid/events/series/$leagueUuid"
            to="./competitors"
            replace
            disabled={!leagueUuid}
          >
            <ListItemDecorator>
              <People />
            </ListItemDecorator>
            Competitors
          </TabLink>
        </TabList>
      </Tabs>
      <Stack direction="row" sx={{ flex: 1, minHeight: 0 }}>
        {leagueSelectorOpen && (
          <CompetitionsList
            activeLeagueUuid={leagueUuid}
            isCreating={isCreate}
            creatingEventType="league"
          />
        )}
        <Box sx={{ flex: 1, display: "flex", minWidth: 0 }}>{children || <Outlet />}</Box>
      </Stack>
    </Box>
  );
};

export default LeagueOverview;
